import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import {
    OrderStatus,
    OrderStatusLabels,
} from '../../../modules/application/orders/shared/constants/order-status';
import { ApplicationPurposeCodeEnum } from '../../../modules/application/shared/constants/application-purpose-codes';
import { AdvisorConstants } from '../../../modules/service-fulfillment/constants/advisor-constants';
import {
    WorkflowState,
    WorkflowStateLabels,
} from '../../../modules/service-fulfillment/constants/workflow-state';
import { ResourceDataKeys } from '../../constants/resource-data-keys';
import {
    InvoiceNet30TransitionablePaymentMethods,
    PaymentMethodLabelMapping,
} from '../../enums/commerce/payment/payment-method';
import {
    AccountingReviewReportExportType,
    AccountingReviewReportExportTypeLabels,
} from '../../enums/commerce/report/accounting-review-report-export-type';
import { formatDateToMMDDYYYY } from '../../functions/date-helpers';
import { getEnumValues } from '../../functions/enum-helper';
import { ContactSearchResponse } from '../../models/civi-crm/contact/contact-search/contact-search-response';
import { OrderCreditCardSummary } from '../../models/commerce/cards-on-file/order-credit-card-summary';
import { Direction } from '../../models/direction';
import { DropdownOption } from '../../models/dropdown-option';
import { DropdownDataOptions } from './models/dropdown-data-options';
import { FiveDayHoldDate } from './models/five-day-hold-date';
import { ReferenceData } from './models/reference-data';
import { ResourcesService } from './resources.service';

@Injectable({
    providedIn: 'root',
})
/**
 * A service responsible for retrieving dropdown-formatted data.
 */
export class DropdownService {
    private readonly resourcesService: ResourcesService =
        inject(ResourcesService);

    private readonly emptyDropdownOption: DropdownOption = {
        value: null,
        label: 'Select An Option',
    };

    getApplicationPurposes(): Observable<DropdownOption[]> {
        return this.resourcesService.getApplicationPurposes().pipe(
            map((data: ReferenceData[]) => {
                return data.map((item: ReferenceData) => {
                    return {
                        value: item.code,
                        label:
                            item.code === ApplicationPurposeCodeEnum.other
                                ? item.description
                                : `${item.code} - ${item.description}`,
                    };
                });
            }),
        );
    }

    getRadioServiceCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.radioServiceCode)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getPublicSafetyCoordinators(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.publicSafetyCoordinators)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getDemonstrationLicenseOrSTA(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.licenseType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getUseOfServices(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.useOfServiceType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getApplicantTypeCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.applicantTypeCode)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    /**
     * Gets the state and state initials from the database, NOT Civi.
     */
    getStateCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.stateCode)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getFrnSearchTypeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.frnSearchType)
            .pipe(
                map((data: ReferenceData[]) =>
                    data.map((item: ReferenceData) => ({
                        value: item.code,
                        label: item.description,
                    })),
                ),
            );
    }

    getActionCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.actionCode)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getYesNoOptions(): Observable<DropdownOption[]> {
        return of(
            this.prefixDropdownOptionsWithEmpty([
                { value: true, label: 'Y - Yes' },
                { value: false, label: 'N - No' },
            ]),
        );
    }

    getLatitudeDirectionOptions(): Observable<DropdownOption[]> {
        return of([
            { value: Direction.North, label: 'N - North' },
            { value: Direction.South, label: 'S - South' },
        ]);
    }

    getLongitudeDirectionOptions(): Observable<DropdownOption[]> {
        return of([
            { value: Direction.East, label: 'E - East' },
            { value: Direction.West, label: 'W - West' },
        ]);
    }

    getLocationTypes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.locationType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getAreaOfOperationCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.areaOfOperationCodes)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getStructureTypeCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.structureTypeCodes)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getSiteStatusCodes(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.siteStatusCodes)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getApplicationTypeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.applicationType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data, {
                        allowEmpty: false,
                    }),
                ),
            );
    }

    getFrequencyAllocationOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.frequencyAllocationCode)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getEligibilityRuleOptions(
        options: DropdownDataOptions | undefined = undefined,
    ): Observable<DropdownOption[]> {
        return this.get(ResourceDataKeys.elibilityRule, options);
    }

    getPurposeOfSubmissionOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.scheduleKPurposes)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getAttachmentCodeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.attachmentCodes)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getCoresRegistrationTypeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.coresRegistrationType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getSalutationOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.salutation)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data, {
                        allowEmpty: true,
                        excludeDescriptions: true,
                    }),
                ),
            );
    }

    getTaxpayerIdExceptionReasonOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.tinExceptionReason)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data).map((option) => ({
                        ...option,
                        value: Number(option.value),
                    })),
                ),
            );
    }

    getBusinessEntityTypeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.businessEntityType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data)
                        .map((option) => ({
                            ...option,
                            value: Number(option.value),
                        }))
                        // The dropdown options return out of order, so sort them based on value
                        .sort((a, b) => a.value - b.value),
                ),
            );
    }

    getEntityTypeOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.entityType)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getSecurityQuestionOptions(): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(ResourceDataKeys.securityQuestion)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data),
                ),
            );
    }

    getOrderStatusOptions(): DropdownOption[] {
        return getEnumValues(OrderStatus).map((status: OrderStatus) => {
            return {
                label: OrderStatusLabels[status],
                value: status,
            };
        });
    }

    getWorkflowStateOptions(): DropdownOption[] {
        return getEnumValues(WorkflowState).map((status: WorkflowState) => {
            return {
                label: WorkflowStateLabels[status],
                value: status,
                disabled: status === WorkflowState.Completed,
            };
        });
    }

    getHandedOverOptions(): DropdownOption[] {
        return [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
        ];
    }

    getAdvisorOptions(): Observable<DropdownOption[]> {
        return this.resourcesService.getAdvisors().pipe(
            map((users) => [
                {
                    label: AdvisorConstants.NoneAssigned,
                    value: null,
                },
                ...users.map((user) => {
                    const advisorName =
                        typeof user.advisorName === 'string'
                            ? user.advisorName.trim()
                            : '';

                    return {
                        label: String(
                            advisorName ||
                                user.userId ||
                                AdvisorConstants.UnnamedAdvisor,
                        ),
                        value: user.userId,
                    };
                }),
            ]),
        );
    }

    /**
     * Retrieve the dropdown options for the Accounting Review Report Export Type. This will return an array of dropdown option objects
     * representing the available export types for the report.
     *
     * @returns An array of DropdownOption objects for the Accounting Review Report Export Type.
     */
    getAccountingReviewReportExportTypeOptions(): DropdownOption[] {
        const dropdownOptions = getEnumValues(
            AccountingReviewReportExportType,
        ).map((option) => ({
            label: AccountingReviewReportExportTypeLabels[option],
            value: option,
        }));

        return this.prefixDropdownOptionsWithEmpty(dropdownOptions);
    }

    /**
     * Retrieve the dropdown options for months. This will be in the format
     * of "01", "02", "03", etc. to ensure that months are zero-padded as expected in date inputs.
     */
    getCreditCardMonthOptions(): DropdownOption[] {
        const totalMonths = 12;
        const monthPadLength = 2;
        const paddingCharacter = '0';

        return Array.from({ length: totalMonths }, (_, i) => {
            const month = String(i + 1).padStart(
                monthPadLength,
                paddingCharacter,
            );

            return { label: month, value: month };
        });
    }

    /**
     * Retrieve the dropdown options for years. This will generate credit card expiration years starting from the current year and
     * extending incrementally for a total of 10 years. The year values will be in a four-digit format (e.g., "2024") as expected in date inputs.
     */
    getCreditCardYearOptions(): DropdownOption[] {
        const currentYear = new Date().getFullYear();
        const totalYears = 10;

        return Array.from({ length: totalYears }, (_, i) => {
            const year = String(currentYear + i);

            return { label: year, value: year };
        });
    }

    getFiveDayHoldDates(): Observable<DropdownOption[]> {
        return this.resourcesService.getFiveDayHoldDates().pipe(
            map((data: FiveDayHoldDate[]) =>
                data.map((date) => ({
                    label: `${formatDateToMMDDYYYY(date.concurrenceDate)} - ${formatDateToMMDDYYYY(date.releaseDate)}`,
                    value: date.id,
                })),
            ),
        );
    }

    getInvoiceNet30TransitionablePaymentMethods(): DropdownOption[] {
        return InvoiceNet30TransitionablePaymentMethods.map((method) => ({
            label: PaymentMethodLabelMapping[method],
            value: method,
        }));
    }

    get(
        key: string,
        options: DropdownDataOptions | undefined = undefined,
    ): Observable<DropdownOption[]> {
        return this.resourcesService
            .getReferenceDataByKey(key)
            .pipe(
                map((data: ReferenceData[]) =>
                    this.transformToDropdownOptions(data, options),
                ),
            );
    }

    transformToDropdownOptions(
        referenceData: ReferenceData[],
        options: DropdownDataOptions | undefined = { allowEmpty: true },
    ): DropdownOption[] {
        const dropdownOptions = referenceData.map((item: ReferenceData) => ({
            value: item.code,
            label: this.getDropdownOptionLabel(item, options),
        }));

        if (options.reverseOrder) {
            dropdownOptions.reverse();
        }

        if (options.allowEmpty) {
            return this.prefixDropdownOptionsWithEmpty(dropdownOptions);
        }

        return dropdownOptions;
    }

    /**
     * Formats the contact label to be displayed in the dropdown.
     *
     * @param contacts The list of contacts to be formatted.
     * @returns An array of dropdown options with name and organization name if available.
     */
    transformContactsToDropdownOptions(
        contacts: ContactSearchResponse[],
    ): DropdownOption[] {
        return contacts.map((contact: ContactSearchResponse) => {
            let contactLabel = `${contact.firstName} ${contact.lastName}`;

            if (contact.organizationName) {
                contactLabel += ` - ${contact.organizationName}`;
            }

            return {
                value: `${contact.id}`,
                label: contactLabel,
            };
        });
    }

    /**
     * Formats the credit card summary data to be displayed in the dropdown, including indicators for expired and default cards.
     * The dropdown label includes the card name, last four digits, expiration date, and suffixes indicating whether the card is
     * expired and/or the default card. Example output: `Visa ending in 1234 exp. 1/2025 (Expired, Default, Selected at Checkout)`.
     *
     * @param creditCards The list of credit card summaries to be formatted for dropdown display.
     * @param isExpired A function that takes an order credit card summary and returns a boolean indicating whether the card is expired.
     * @returns An array of dropdown options with formatted labels for each credit card summary, including indicators for expired and default cards.
     */
    transformEligibleCardsForOrderToDropdownOptions(
        creditCards: OrderCreditCardSummary[],
        isExpired: (card: OrderCreditCardSummary) => boolean,
    ): DropdownOption[] {
        return creditCards.map((card: OrderCreditCardSummary) => {
            // Indicate in the dropdown label if the card is expired and/or the default card.
            // For example: "Visa - Ending in 1234 (Exp: 01/2025) (Default, Expired, Selected at Checkout)"
            const suffixes = [
                isExpired(card) ? 'Expired' : '',
                card.isDefault ? 'Default' : '',
                card.isPreferred ? 'Selected at Checkout' : '',
            ].filter(Boolean);

            // Join the suffixes with a comma if there are multiple, and wrap in parentheses.
            // If there are no suffixes, this will be an empty string.
            const suffix = suffixes.length ? ` (${suffixes.join(', ')})` : '';

            return {
                label: `${card.cardName} - Ending in ${card.truncatedCardNumber} (Exp: ${card.expirationMonth}/${card.expirationYear})${suffix}`,
                value: card.id,
            };
        });
    }

    /**
     * Prepends the default dropdown option to the dropdown options that the user will be able to use to deselect.
     *
     * @param dropdownOptionData the dropdown options that the user will be able to select
     * @returns the dropdown  options that were passed in with the default dropdown option prepended
     */
    prefixDropdownOptionsWithEmpty(
        dropdownOptionData: DropdownOption[],
    ): DropdownOption[] {
        return [this.emptyDropdownOption, ...dropdownOptionData];
    }

    /**
     * Gets the label for a dropdown option based on the data options.
     *
     * @param option
     * @param dataOptions
     * @returns
     */
    private getDropdownOptionLabel(
        option: ReferenceData,
        dataOptions: DropdownDataOptions,
    ): string {
        return dataOptions.excludeDescriptions === true
            ? option.code
            : `${option.code} - ${option.description}`;
    }
}
